import React, { useState, useEffect } from 'react';
import { Avatar, Box, Stack, Typography, alpha } from '@mui/material';

import {
    DashboardMenuItem,
    MenuProps,
    useSidebarState,
} from 'react-admin';

import MenuContainer from './MenuContainer';
import menuitems from './menuitems.json';
import { MenuItem } from '../types';
import { getRoleId, isSchoolUser, getSchool } from '../common/schoolRoles';
import { sidebarColors } from './appBarTheme';

const Menu = ({ dense = false }: MenuProps) => {
    const [rights, setRights] = useState([]);
    const school = getSchool();
    const [categories, setCategories] = useState<string[]>([]);

    const [open] = useSidebarState();

    /**
     * SetMenuData - Filters menu items based on the effective role.
     *
     * TWO KINDS OF ENTRY, TOLD APART BY `Roles`
     * -----------------------------------------
     * An entry WITH a `Roles` array is school-scoped: it lives in the school's
     * own database and is reachable only by a session bound to a school.
     * An entry WITHOUT one is WiZi-owned and lives in Master.
     *
     * That split is not cosmetic. A school session is bound to its own
     * database, so a WiZi screen opened from it would query the wrong place --
     * and a WiZi session has no school binding at all, so a school screen
     * opened from it gets a 403 from every endpoint behind it. Neither set is
     * merely inappropriate for the other role; both are broken for it.
     *
     *   role 1        WiZi super admin -- every WiZi entry, no school entries
     *   role 20 / 21  school admin or teacher -- only entries naming their role
     *   anything else WiZi staff -- Question, Chapter, Topic
     *
     * School users previously got an empty menu, because until Phase 2 there
     * were no school-owned resources to list. Now there are.
     *
     * This is presentation only. The API refuses each of these endpoints on the
     * role in the signed token, so hiding an entry hides a link and nothing
     * more -- which is the right division: the menu is a convenience, and
     * require_school_role() is the control.
     */
    const SetMenuData = () => {
        let menuData: MenuItem[] = menuitems as MenuItem[];

        const roleId = getRoleId();

        if (isSchoolUser(roleId)) {
            menuData = menuData.filter((item: MenuItem) =>
                Array.isArray(item.Roles) && roleId !== null && item.Roles.includes(roleId)
            );
        } else {
            // WiZi staff never see school-scoped entries: they have no school
            // binding, so every endpoint behind them would refuse.
            menuData = menuData.filter((item: MenuItem) => !Array.isArray(item.Roles));

            if (roleId !== 1) {
                const allowedModules = ['Question', 'Chapter', 'Topic'];
                menuData = menuData.filter((item: MenuItem) =>
                    allowedModules.includes(item.Module)
                );
            }
        }

        localStorage.setItem("menu", JSON.stringify(menuData));
        var outObject = menuData.reduce(function (a: any, e: MenuItem) {
            let estKey = (e['ModuleCategory']);
            if (a[estKey] == undefined)
                a[estKey] = [];
            a[estKey].push(e);
            return a;
        }, {});
        var keys = Object.keys(outObject);
        setCategories(keys);
        setRights(outObject);
    }

    useEffect(() => {
        SetMenuData();
    }, []);

    const onMenuClick = () => {};

    const schoolInitial = (school?.name || school?.code || '-').trim().charAt(0).toUpperCase();

    return (
        <Box
            sx={theme => {
                const { fg } = sidebarColors(theme);
                return {
                    width: open ? 260 : 56,
                    minHeight: '100%',
                    pt: 1,
                    pb: 2,
                    color: fg,
                    // The band itself is painted on the drawer in Layout.tsx,
                    // which is the only place with enough specificity to beat
                    // the per-theme sidebar overrides. This stays transparent
                    // so it cannot seam where the menu ends.
                    backgroundColor: 'transparent',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                };
            }}
        >
            {/* Which school this session is bound to.
                This block used to be two default-coloured Typographies, which
                rendered in the theme's dark text on the dark sidebar and were
                effectively unreadable. Colours are explicit now. */}
            {school && open && (
                <Box
                    sx={theme => {
                        const { fg } = sidebarColors(theme);
                        return {
                            mx: 1.5,
                            mb: 1.5,
                            px: 1.5,
                            py: 1.25,
                            borderRadius: 2.5,
                            bgcolor: alpha(fg, 0.1),
                            border: `1px solid ${alpha(fg, 0.16)}`,
                        };
                    }}
                >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar
                            variant="rounded"
                            sx={theme => {
                                const { fg, top } = sidebarColors(theme);
                                return {
                                    width: 34,
                                    height: 34,
                                    fontSize: 14,
                                    fontWeight: 800,
                                    bgcolor: alpha(fg, 0.9),
                                    color: top,
                                };
                            }}
                        >
                            {schoolInitial}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant="caption"
                                sx={theme => ({
                                    display: 'block',
                                    fontWeight: 700,
                                    letterSpacing: '.1em',
                                    color: alpha(sidebarColors(theme).fg, 0.65),
                                })}
                            >
                                {school.code}
                            </Typography>
                            <Typography
                                variant="body2"
                                noWrap
                                title={school.name}
                                sx={theme => ({
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    color: sidebarColors(theme).fg,
                                })}
                            >
                                {school.name}
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            )}

            <Box
                sx={theme => {
                    const { fg } = sidebarColors(theme);
                    return {
                        // Dashboard is a RaMenuItemLink too, so it picks up the
                        // same white left border radiantTheme puts on the rest.
                        '&& .RaMenuItemLink-root, && .MuiMenuItem-root': {
                            mx: 1,
                            borderRadius: 2,
                            borderLeft: 'none',
                            color: alpha(fg, 0.82),
                        },
                        '& .MuiListItemIcon-root': { minWidth: 30, color: 'inherit' },
                        '&& .RaMenuItemLink-active': {
                            bgcolor: alpha(fg, 0.16),
                            backgroundImage: 'none',
                            color: fg,
                            fontWeight: 700,
                            borderLeft: 'none',
                            borderRadius: 2,
                            boxShadow: 'none',
                        },
                        '& .MuiMenuItem-root:hover': { bgcolor: alpha(fg, 0.09) },
                    };
                }}
            >
                <DashboardMenuItem onClick={onMenuClick} />
            </Box>

            <Box
                sx={theme => ({
                    height: '1px',
                    mx: 2,
                    my: 1,
                    bgcolor: alpha(sidebarColors(theme).fg, 0.15),
                })}
            />

            {categories && categories.map((item: any, index: number) => {
                return (
                    <MenuContainer key={index} onMenuClick={onMenuClick} sidebarIsOpen={open} dense={dense} caption={item} items={rights[item]} />
                )
            })}
        </Box>
    );
};

export default Menu;
